import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Save, Lock, Activity, Upload, FileText, Trash2, LifeBuoy, LogOut } from "lucide-react";
import SupportFeedbackDialog from "@/components/SupportFeedbackDialog";
import TagInput from "@/components/TagInput";
import NotificationSettings from "@/components/NotificationSettings";
import { API } from "@/lib/api";

const ACTIVITY = [
  { v: "sedentary", l: "Sedentary (little/no exercise)" },
  { v: "light", l: "Light (1-3 days/week)" },
  { v: "moderate", l: "Moderate (3-5 days/week)" },
  { v: "very_active", l: "Very active (6-7 days/week)" },
  { v: "extra_active", l: "Extra active (athlete / 2x day)" },
];
const GOAL = [
  { v: "lose", l: "Lose weight" },
  { v: "maintain", l: "Maintain" },
  { v: "gain", l: "Gain weight / muscle" },
];
const DIET = [
  { v: "veg", l: "Vegetarian" },
  { v: "non_veg", l: "Non-vegetarian" },
  { v: "eggitarian", l: "Eggitarian" },
];

export default function Profile() {
  const { refresh, logout, user } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/profile");
    setP(data);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        age: p.age ? Number(p.age) : null,
        gender: p.gender || null,
        height_cm: p.height_cm ? Number(p.height_cm) : null,
        weight_kg: p.weight_kg ? Number(p.weight_kg) : null,
        target_weight_kg: p.target_weight_kg ? Number(p.target_weight_kg) : null,
        activity_level: p.activity_level || null,
        goal: p.goal || null,
        dietary_preference: p.dietary_preference || null,
        city: p.city || null,
        region: p.region || null,
        country: p.country || null,
        allergies: p.allergies || [],
        cuisines: p.cuisines || [],
        food_preferences: p.food_preferences || [],
        preferred_ingredients: p.preferred_ingredients || [],
        disliked_foods: p.disliked_foods || [],
        medical_conditions: p.medical_conditions || [],
        medications: p.medications || [],
        grocery_sources: p.grocery_sources || [],
        custom_tags: p.custom_tags || {},
        bmi_override: p.bmi_override ?? null,
        bmr_override: p.bmr_override ?? null,
        tdee_override: p.tdee_override ?? null,
        notes: p.notes || "",
      };
      const { data } = await api.put("/profile", payload);
      setP(data);
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const recalc = async () => {
    setRecalcing(true);
    try {
      const { data } = await api.post("/profile/recalc-macros");
      setP(data);
      toast.success("Macros recalculated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't recalc");
    } finally {
      setRecalcing(false);
    }
  };

  if (!p) {
    return <Layout><div className="max-w-3xl mx-auto px-6 py-16 text-muted-foreground">Loading…</div></Layout>;
  }

  const mt = p.macro_targets || {};
  const locked = !!p.macros_admin_locked;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-overline">your profile</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight">Tell us about you</h1>
        <p className="text-foreground/70 mt-1 mb-8">Your details power personalized macro targets, recipes, and coaching. Everything here is editable any time.</p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-surface-elevated border border-border rounded-3xl p-6 space-y-4">
            <div className="text-overline mb-1">basics</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Age</Label><Input type="number" value={p.age || ""} onChange={(e) => set("age", e.target.value)} data-testid="prof-age" /></div>
              <div><Label>Gender</Label>
                <Select value={p.gender || ""} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger data-testid="prof-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Height (cm)</Label><Input type="number" value={p.height_cm || ""} onChange={(e) => set("height_cm", e.target.value)} data-testid="prof-height" /></div>
              <div><Label>Current weight (kg)</Label><Input type="number" value={p.weight_kg || ""} onChange={(e) => set("weight_kg", e.target.value)} data-testid="prof-weight" /></div>
              <div><Label>Target weight (kg)</Label><Input type="number" value={p.target_weight_kg || ""} onChange={(e) => set("target_weight_kg", e.target.value)} data-testid="prof-target" /></div>
              <div><Label>Activity level</Label>
                <Select value={p.activity_level || ""} onValueChange={(v) => set("activity_level", v)}>
                  <SelectTrigger data-testid="prof-activity"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{ACTIVITY.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Goal</Label>
                <Select value={p.goal || ""} onValueChange={(v) => set("goal", v)}>
                  <SelectTrigger data-testid="prof-goal"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{GOAL.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Diet</Label>
                <Select value={p.dietary_preference || ""} onValueChange={(v) => set("dietary_preference", v)}>
                  <SelectTrigger data-testid="prof-diet"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{DIET.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Allergies</Label>
                <TagInput value={p.allergies || []} onChange={(v) => set("allergies", v)} placeholder="e.g., peanuts, shellfish" testid="prof-allergies" />
              </div>
              <div><Label>City</Label><Input value={p.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="Bengaluru" data-testid="prof-city" /></div>
              <div><Label>State / region</Label><Input value={p.region || ""} onChange={(e) => set("region", e.target.value)} placeholder="Karnataka" data-testid="prof-region" /></div>
              <div className="col-span-2"><Label>Country</Label><Input value={p.country || ""} onChange={(e) => set("country", e.target.value)} placeholder="India" data-testid="prof-country" /></div>
              <div className="col-span-2"><Label>Notes (the assistant may also add to this)</Label>
                <Textarea rows={3} value={p.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Preferences, dislikes, medical context…" className="rounded-xl bg-surface" data-testid="prof-notes" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-profile-btn">
                <Save size={16} className="mr-1" /> {saving ? "Saving…" : "Save profile"}
              </Button>
            </div>
          </div>

          <div className="bg-surface-elevated border border-border rounded-3xl p-6 h-fit">
            <div className="flex items-center justify-between mb-2">
              <div className="text-overline">daily macro targets</div>
              {locked && <Lock size={14} className="text-secondary" title="Set by admin" />}
            </div>
            <MacroEditor p={p} setP={setP} refresh={refresh} />
            <p className="text-xs text-muted-foreground mt-3">
              {locked
                ? "Your admin has set custom targets. Ask them to adjust if needed."
                : p.macros_user_set
                  ? "Manually pinned. Auto-recalculation is off until you reset."
                  : "Auto-derived from Mifflin-St Jeor BMR + activity factor + your goal."}
            </p>
          </div>

          {/* Preferences & Health */}
          <div className="md:col-span-3 bg-surface-elevated border border-border rounded-3xl p-6">
            <div className="text-overline mb-1">preferences &amp; health</div>
            <p className="text-xs text-muted-foreground mb-4">Edit or add tags any time. The assistant will keep enriching these as you chat — your inputs are never replaced, only appended.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Cuisines you love</Label>
                <TagInput value={p.cuisines || []} onChange={(v) => set("cuisines", v)} placeholder="South Indian, Mediterranean…" testid="prof-cuisines" />
              </div>
              <div><Label>Food preferences</Label>
                <TagInput value={p.food_preferences || []} onChange={(v) => set("food_preferences", v)} placeholder="spicy, low-sodium, high-protein…" testid="prof-foodprefs" />
              </div>
              <div><Label>Preferred ingredients</Label>
                <TagInput value={p.preferred_ingredients || []} onChange={(v) => set("preferred_ingredients", v)} placeholder="olive oil, paneer, lentils…" testid="prof-ingredients" />
              </div>
              <div><Label>Disliked foods</Label>
                <TagInput value={p.disliked_foods || []} onChange={(v) => set("disliked_foods", v)} placeholder="bell peppers, mushrooms…" testid="prof-dislikes" />
              </div>
              <div><Label>Medical conditions</Label>
                <TagInput value={p.medical_conditions || []} onChange={(v) => set("medical_conditions", v)} placeholder="diabetic, PCOS, hypertension…" testid="prof-conditions" />
              </div>
              <div><Label>Medications</Label>
                <TagInput value={p.medications || []} onChange={(v) => set("medications", v)} placeholder="metformin, thyroxine…" testid="prof-meds" />
              </div>
              <div className="md:col-span-2"><Label>Grocery sources</Label>
                <TagInput value={p.grocery_sources || []} onChange={(v) => set("grocery_sources", v)} placeholder="BigBasket, BlinkIt, Whole Foods…" testid="prof-grocery" />
              </div>
            </div>
            {p.custom_tags && Object.keys(p.custom_tags).length > 0 && (
              <div className="mt-5 border-t border-border pt-4">
                <div className="text-overline mb-2">other tags (auto-collected)</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {Object.entries(p.custom_tags).map(([cat, tags]) => (
                    <div key={cat}><Label className="capitalize">{cat.replace(/_/g, " ")}</Label>
                      <TagInput
                        value={tags || []}
                        onChange={(v) => set("custom_tags", { ...(p.custom_tags || {}), [cat]: v })}
                        testid={`prof-ct-${cat}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button onClick={save} disabled={saving} variant="outline" className="rounded-full" data-testid="save-profile-btn-2">
                <Save size={16} className="mr-1" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>

          {/* Health Metrics — BMI/BMR/TDEE */}
          <div className="md:col-span-3 bg-surface-elevated border border-border rounded-3xl p-6">
            <div className="text-overline mb-1 flex items-center gap-1"><Activity size={12} /> health metrics</div>
            <p className="text-xs text-muted-foreground mb-4">Derived automatically from your basics — but each is editable if you have a clinical measurement.</p>
            <MetricsGrid p={p} set={set} onSave={save} />
          </div>

          {/* Locale & Clinical assessments */}
          <div className="md:col-span-3">
            <Assessments p={p} onRefreshed={async () => { await load(); }} />
          </div>

          {/* Medical Reports */}
          <div className="md:col-span-3">
            <MedicalReports onMerged={async () => { await load(); await refresh(); }} />
          </div>

          {/* Notification preferences + device test panel */}
          <div className="md:col-span-3">
            <NotificationSettings />
          </div>

          {/* Help & support */}
          <div className="md:col-span-3 bg-surface-elevated border border-border rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="profile-support-card">
            <div>
              <div className="text-overline mb-1 flex items-center gap-1"><LifeBuoy size={12} /> help &amp; support</div>
              <p className="text-sm text-foreground/70">Have a question, hit a snag, or want to share an idea? Our team reads every message.</p>
            </div>
            <Button onClick={() => setSupportOpen(true)} className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground shrink-0" data-testid="profile-support-btn">
              <LifeBuoy size={15} className="mr-1.5" /> Contact support
            </Button>
          </div>

          {/* Sign out */}
          <div className="md:col-span-3 bg-surface-elevated border border-border rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="profile-signout-card">
            <div>
              <div className="text-overline mb-1 flex items-center gap-1"><LogOut size={12} /> account</div>
              <p className="text-sm text-foreground/70">Signed in as <span className="font-medium text-foreground/90">{user?.email || user?.name}</span>. Sign out on this device anytime.</p>
            </div>
            <Button onClick={logout} variant="outline" className="rounded-full border-border text-foreground/80 hover:bg-muted shrink-0" data-testid="profile-signout-btn">
              <LogOut size={15} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </div>
      <SupportFeedbackDialog open={supportOpen} onOpenChange={setSupportOpen} />
    </Layout>
  );
}

function MetricsGrid({ p, set, onSave }) {
  const hm = p.health_metrics || {};
  const Card = ({ label, value, hint, field, unit }) => {
    const override = p[field];
    const display = override ?? value;
    return (
      <div className="bg-surface rounded-2xl p-4 border border-border" data-testid={`metric-${field}`}>
        <div className="text-overline">{label}</div>
        <div className="flex items-baseline gap-1 mt-1">
          <Input
            type="number"
            value={display ?? ""}
            onChange={(e) => set(field, e.target.value ? Number(e.target.value) : null)}
            className="h-9 font-serif-display text-2xl text-primary border-0 bg-transparent p-0 focus-visible:ring-0"
            data-testid={`metric-input-${field}`}
            placeholder="—"
          />
          <span className="text-xs text-muted-foreground">{unit}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{hint}</div>
      </div>
    );
  };
  return (
    <>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="BMI" value={hm.bmi} unit="" field="bmi_override" hint={bmiBand(p.bmi_override ?? hm.bmi)} />
        <Card label="BMR" value={hm.bmr} unit="kcal/day" field="bmr_override" hint="resting energy" />
        <Card label="TDEE" value={hm.tdee} unit="kcal/day" field="tdee_override" hint="incl. activity" />
        <div className="bg-surface rounded-2xl p-4 border border-border">
          <div className="text-overline">Ideal weight</div>
          <div className="font-serif-display text-2xl text-primary mt-1">{hm.ideal_weight_kg ?? "—"}<span className="text-xs text-muted-foreground ml-1">kg</span></div>
          <div className="text-xs text-muted-foreground mt-1">Devine formula</div>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={onSave} variant="outline" className="rounded-full" data-testid="save-metrics-btn">
          <Save size={14} className="mr-1" /> Save metrics
        </Button>
      </div>
    </>
  );
}

function bmiBand(v) {
  if (v == null) return "—";
  if (v < 18.5) return "underweight";
  if (v < 25) return "normal";
  if (v < 30) return "overweight";
  return "obese";
}

function MedicalReports({ onMerged }) {
  const [reports, setReports] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/profile/medical-reports");
    setReports(data || []);
  };
  useEffect(() => { load(); }, []);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Max 20MB");
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("PDF only");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tuha_token");
      const res = await fetch(`${API}/profile/medical-reports`, {
        method: "POST", credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      toast.success("Uploaded — extracting findings in the background…");
      await load();
      // Poll for extraction completion (≤90s)
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: latest } = await api.get(`/profile/medical-reports/${created.id}`);
          if (latest.extraction_status === "completed") {
            toast.success("Findings extracted — profile enriched");
            await load();
            onMerged?.();
            break;
          }
          if (latest.extraction_status === "failed") {
            toast.error("Could not extract findings from this PDF");
            await load();
            break;
          }
        } catch (_) { /* keep polling */ }
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    await api.delete(`/profile/medical-reports/${id}`);
    load();
  };

  return (
    <div className="bg-surface-elevated border border-border rounded-3xl p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-overline">medical reports</div>
          <div className="text-xs text-muted-foreground">Upload labs or scans (PDF, ≤20MB). The assistant extracts conditions, medications, and allergies — merged into your profile.</div>
        </div>
        <label className="cursor-pointer">
          <Button asChild className="rounded-full bg-primary hover:bg-primary/90 pointer-events-none" data-testid="upload-medrep-btn">
            <span>{uploading ? "Uploading…" : (<><Upload size={14} className="mr-1" /> Upload PDF</>)}</span>
          </Button>
          <input type="file" accept="application/pdf" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="medrep-file-input" />
        </label>
      </div>
      {reports.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-2xl p-6 text-center">No reports yet. Drop a PDF lab report or imaging summary to enrich your profile.</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border" data-testid="medrep-table">
          <table className="w-full text-sm">
            <thead className="bg-surface">
              <tr>
                <th className="text-overline px-3 py-2 text-left">Uploaded</th>
                <th className="text-overline px-3 py-2 text-left">Report date</th>
                <th className="text-overline px-3 py-2 text-left">File</th>
                <th className="text-overline px-3 py-2 text-left">Summary</th>
                <th className="text-overline px-3 py-2 text-left">Findings</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-border align-top" data-testid={`medrep-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground text-xs">{new Date(r.uploaded_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.report_date || "—"}</td>
                  <td className="px-3 py-2 flex items-center gap-1.5 min-w-[180px]"><FileText size={14} className="text-secondary" /> <span className="truncate">{r.file_name}</span></td>
                  <td className="px-3 py-2 max-w-[280px]"><div className="line-clamp-3 text-foreground/80">
                    {r.extraction_status === "pending" ? <span className="text-secondary italic">Extracting…</span>
                      : r.extraction_status === "failed" ? <span className="text-destructive italic">Extraction failed</span>
                      : (r.extracted_summary || "—")}
                  </div></td>
                  <td className="px-3 py-2 max-w-[260px]">
                    <div className="flex flex-wrap gap-1">
                      {(r.extracted_findings?.conditions || []).slice(0, 3).map((c, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive">{c}</span>)}
                      {(r.extracted_findings?.medications || []).slice(0, 3).map((c, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary">{c}</span>)}
                      {(r.extracted_findings?.lab_flags || []).slice(0, 2).map((c, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 rounded-full bg-accent/20 text-accent-foreground">{c}</span>)}
                    </div>
                  </td>
                  <td className="px-3 py-2"><Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`medrep-del-${r.id}`}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Assessments({ p, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const la = p.locale_assessment || {};
  const ca = p.clinical_assessment || {};
  const hasLa = (la.seasonal_produce || []).length || (la.local_staples || []).length;
  const hasCa = ["recommended", "fine", "neutral", "avoid"].some((k) => (ca[k] || []).length);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/profile/assessments/refresh");
      toast.success("Assessments refreshed");
      await onRefreshed?.();
    } catch (e) {
      toast.error("Couldn't refresh — make sure location and health details are filled");
    } finally {
      setBusy(false);
    }
  };

  const Chips = ({ items, tone }) => (
    <div className="flex flex-wrap gap-1.5">
      {(items || []).map((x, i) => <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full ${tone}`}>{x}</span>)}
      {(!items || items.length === 0) && <span className="text-xs text-muted-foreground">—</span>}
    </div>
  );

  return (
    <div className="bg-surface-elevated border border-border rounded-3xl p-6" data-testid="assessments-card">
      <div className="flex items-center justify-between mb-1">
        <div className="text-overline flex items-center gap-1"><Sparkles size={12} /> locale & clinical assessments</div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy} className="rounded-full" data-testid="refresh-assessments-btn">
          {busy ? "Assessing…" : "Refresh"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Two AI agents assess what's locally in-season near you and which ingredients suit your health — then feed this into every recipe & meal plan. Auto-updates when you change your location or health details.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-surface rounded-2xl p-4 border border-border" data-testid="locale-assessment">
          <div className="text-sm font-medium text-primary mb-1">Locally available & in-season</div>
          {hasLa ? (
            <>
              <div className="text-xs text-muted-foreground mb-2">{la.location} · {la.month} · {la.season}</div>
              <div className="text-overline mt-2">in-season produce</div>
              <Chips items={la.seasonal_produce} tone="bg-secondary/15 text-secondary" />
              <div className="text-overline mt-3">local staples</div>
              <Chips items={la.local_staples} tone="bg-muted text-foreground/70" />
            </>
          ) : <div className="text-sm text-muted-foreground">Add your city + country, then Refresh.</div>}
        </div>
        <div className="bg-surface rounded-2xl p-4 border border-border" data-testid="clinical-assessment">
          <div className="text-sm font-medium text-primary mb-1">Clinical ingredient guidance</div>
          {hasCa ? (
            <>
              <div className="text-overline mt-2 text-destructive">avoid</div>
              <Chips items={ca.avoid} tone="bg-destructive/10 text-destructive" />
              <div className="text-overline mt-3">recommended</div>
              <Chips items={ca.recommended} tone="bg-secondary/15 text-secondary" />
              <div className="text-overline mt-3">limit / neutral</div>
              <Chips items={ca.neutral} tone="bg-accent/20 text-accent-foreground" />
              {ca.rationale && <div className="text-xs text-muted-foreground mt-3 italic">{ca.rationale}</div>}
            </>
          ) : <div className="text-sm text-muted-foreground">Add health conditions / goal, then Refresh.</div>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, unit }) {
  return (
    <div className="flex items-baseline justify-between border-b border-border last:border-b-0 pb-2">
      <span className="text-overline">{label}</span>
      <span><span className="font-serif-display text-2xl text-primary">{value}</span> <span className="text-xs text-muted-foreground">{unit}</span></span>
    </div>
  );
}

function MacroEditor({ p, setP, refresh }) {
  const mt = p.macro_targets || {};
  const [draft, setDraft] = useState({
    calories: Math.round(mt.calories || 0),
    protein_g: Math.round(mt.protein_g || 0),
    carbs_g: Math.round(mt.carbs_g || 0),
    fat_g: Math.round(mt.fat_g || 0),
    fiber_g: Math.round(mt.fiber_g || 0),
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const locked = !!p.macros_admin_locked;

  useEffect(() => {
    setDraft({
      calories: Math.round(mt.calories || 0),
      protein_g: Math.round(mt.protein_g || 0),
      carbs_g: Math.round(mt.carbs_g || 0),
      fat_g: Math.round(mt.fat_g || 0),
      fiber_g: Math.round(mt.fiber_g || 0),
    });
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mt.calories, mt.protein_g, mt.carbs_g, mt.fat_g]);

  const set = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };

  const saveTargets = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile/macros", draft);
      setP(data);
      await refresh();
      toast.success("Macro targets saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToAuto = async () => {
    setRecalcing(true);
    try {
      const { data } = await api.post("/profile/macros/reset");
      setP(data);
      await refresh();
      toast.success("Reset to auto-calculated targets");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setRecalcing(false);
    }
  };

  const Row = ({ label, field, unit }) => (
    <div className="flex items-baseline justify-between border-b border-border last:border-b-0 py-2 gap-2">
      <span className="text-overline">{label}</span>
      <div className="flex items-baseline gap-1">
        <Input
          type="number"
          disabled={locked}
          value={draft[field]}
          onChange={(e) => set(field, Number(e.target.value))}
          className="h-9 w-24 font-serif-display text-2xl text-primary text-right border-0 bg-transparent p-0 focus-visible:ring-0"
          data-testid={`macro-${field}`}
        />
        <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
    </div>
  );

  return (
    <>
      <div>
        <Row label="Calories" field="calories" unit="kcal" />
        <Row label="Protein" field="protein_g" unit="g" />
        <Row label="Carbs" field="carbs_g" unit="g" />
        <Row label="Fat" field="fat_g" unit="g" />
        <Row label="Fiber" field="fiber_g" unit="g" />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <Button onClick={saveTargets} disabled={locked || !dirty || saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-macros-btn">
          {saving ? "Saving…" : (p.macros_user_set ? "Update pin" : "Pin targets")}
        </Button>
        <Button onClick={resetToAuto} variant="outline" disabled={locked || recalcing} className="rounded-full" data-testid="reset-macros-btn">
          <Sparkles size={14} className="mr-1" /> {recalcing ? "Recalc…" : (p.macros_user_set ? "Reset to auto" : "Recalculate")}
        </Button>
      </div>
    </>
  );
}
